import { Provider } from '@angular/core'

import {
  AppContext,
  Utils,
  ObjectHelper,
  ObservableUtils,
  HttpHelper,
  TypeOf,
  DependencyContainer
} from '../../../helpers'

import { BaseModel } from '../../../data/models'

import {
  EntityMappingFactory,
  ModelIncludeFactory,
  IDataServiceConfig,
  DataServiceBuilder,
  ModelRepositoryAdapter,
  ModelRepository,
  ModelIdentityMap,
} from '../../../model-repository'

import { ProviderHelper } from '../provider-helper'

export class DataServiceProvider {
  private _providerHelper: ProviderHelper<DataServiceProvider>
  private _dependencyContainer: DependencyContainer

  constructor(
    private _apiRoot: string,
    private _dataServiceConfigs: Array<IDataServiceConfig<any>>,
    private _identityMap?: ModelIdentityMap
  ) {
    this._providerHelper = new ProviderHelper(DataServiceProvider, this)
    this._dependencyContainer = new DependencyContainer()
  }

  public createAngularProviders() {
    const builderDependencies = [AppContext, HttpHelper, Utils, ObservableUtils, ObjectHelper]
    const providers: Provider[] = [
      {
        provide: DataServiceBuilder,
        deps: builderDependencies,
        useFactory: this._providerHelper.withEnsuredDIDependencies(
          builderDependencies, (
            appContext: AppContext,
            httpHelper: HttpHelper,
            utils: Utils,
            observableUtils: ObservableUtils,
            objectHelper: ObjectHelper,
          ) => {
            return this._dataServiceBuilderFactory(
              appContext,
              httpHelper,
              utils,
              observableUtils,
              objectHelper
            )
          }
        )
      }
    ]
    // TODO [GitHub Issue]: Require all the services' dependencies once | A more ideal approach
    // would instantiate all the services' dependencies first and add them to the dependency container.
    // Once all the dependencies are set up, the services can be instantiated only using the container.
    // However, we don't know the order in which Angular processes the dependencies
    //
    // TODO [GitHub Issue]: Perhaps this should rather call `build` instead `buildClass` and take
    // care of dependency handling itself
    const factoryDependencies = [DataServiceBuilder]
    this._dataServiceConfigs.forEach((dataServiceConfig) => {
      const serviceDependencies = Array.isArray(dataServiceConfig.deps) ? dataServiceConfig.deps : []
      // We don't want angular to resolve the Repository as it'll be private in here
      // Technically this should be "serviceDependencies - dependencies already in this._dependencyContainer"
      const angularProvidedDependencies = serviceDependencies.filter((d) => d !== ModelRepository)
      providers.push(
        {
          provide: dataServiceConfig.service,
          deps: factoryDependencies.concat(angularProvidedDependencies),
          useFactory: this._dataServiceFactoryFactory(dataServiceConfig.service, angularProvidedDependencies)
        }
      )
    })
    return providers
  }

  private _dataServiceBuilderFactory(
    appContext: AppContext,
    httpHelper: HttpHelper,
    utils: Utils,
    observableUtils: ObservableUtils,
    objectHelper: ObjectHelper
  ): DataServiceBuilder {

    const builder = new DataServiceBuilder(
      this._apiRoot,
      appContext,
      httpHelper,
      utils,
      observableUtils,
      new ModelIncludeFactory(),
      new EntityMappingFactory(utils, objectHelper),
      this._dependencyContainer,
      this._identityMap
    )
    builder.addServiceConfigs(this._dataServiceConfigs)
    return builder
  }

  private _dataServiceFactoryFactory<A extends ModelRepositoryAdapter<BaseModel>>(
    serviceKlass: TypeOf<A>,
    angularProvidedDependencies: Array<TypeOf<any>>
  ): (dataServiceBuilder: DataServiceBuilder, ...serviceDependencyValues: any[]) => A {

    return (dataServiceBuilder: DataServiceBuilder, ...angularProvidedDependencyValues: any[]) => {
      // Add Angular provided dependencies to internal dependency container as they are now available.
      // This mutates the global state when it would ideally be set already.
      // However, the same dependency may be added multiple times
      angularProvidedDependencies.forEach((dep, i) => {
        this._dependencyContainer.provideValue(dep, angularProvidedDependencyValues[i])
      })
      return dataServiceBuilder.buildClass(serviceKlass).instance
    }
  }
}



// WEBPACK FOOTER //
// ./node_modules/@mprep/shared/lib/angular/providers/data-service/data-service.provider.ts